/*
 * The rail: the one column with a colour, and the column of *places* beside everything (UX §1.1,
 * VISUAL.md §7.1). A place is somewhere a reader goes and stays — Home, Activity, AI Chats, Search,
 * and Administration for whoever has it. Everything else the rail once carried is a tool rather than a
 * place and lives in the account menu now: the directory, the keyboard sheet, the settings, the two
 * ways out. People is deliberately not a row (goal: "not too many tabs"), so the rail stays four
 * rows for a team member — Home, Activity, AI Chats, Search — and five for an administrator.
 * A place of a module the instance does not run is not drawn, and AI Chats is a team member's
 * place: a guest never sees it, whatever groups they are in.
 *
 * It is a `<nav>` with real links, so middle-click, "open in a new tab" and the browser's own
 * history all work; the icons are decorative and each link carries its name as text. The head is
 * the mark — the rocket over the wordmark — and is the way home, the way every chat application's
 * mark is.
 *
 * An entry is active over a *prefix set* rather than over its own path alone: a conversation is
 * what Home shows, so Home stays lit on `/c/…` and `/d/…`. Without that, the rail went blank the
 * moment a conversation was opened, which is the state a reader spends the day in (A-06 D3).
 *
 * Below 768 px the same list becomes the bottom bar. The names are drawn there too — four
 * unlabelled glyphs is not a navigation — and the account entry is the face captioned "You",
 * because a display name is the one caption the copy does not control (D-06 §2.15). The bar carries
 * one entry the column does not need: *Chats*, the door to the conversation list, which below 768 px
 * is not a column beside the screen but a sheet over it (VISUAL.md §7.19). Without that door a
 * conversation is reachable on a phone only while Home still offers it, and a reader who has read
 * everything has no list at all (X-B3 A1).
 */

import { Link, useRouterState } from "@tanstack/react-router"
import { Fragment, useMemo } from "react"
import { cx } from "../../lib/cx.ts"
// Deep import on purpose, as every core file that needs an address does: `routes/index.ts` reaches
// the router, and the router renders this shell.
import { AI_PLACE, DRIVE_PLACE } from "../../routes/links.ts"
import { unreadBadge } from "../activity/feed.ts"
import { formatCapped } from "../i18n/format.ts"
import { t, type PlainKey } from "../i18n/index.ts"
import type { ModuleId, ModuleRegistry } from "../modules/registry.ts"
import { moduleActive } from "../modules/slots.ts"
import { isAdmin, isTeamMember } from "../people/standing.ts"
import type { Model } from "../store/model.ts"
import { catchUpTotals } from "../store/selectors.ts"
import { AccountMenu } from "./AccountMenu.tsx"
import { useModel, useShell } from "./context.tsx"
import { Icon, type IconName } from "./icons.tsx"
import { useLayoutMode } from "./layout.ts"
import "./shell.css"
import { Wordmark } from "./Wordmark.tsx"

interface RailItem {
	to: string
	icon: IconName
	labelKey: PlainKey
	/**
	 * A shorter caption for the bottom bar, where a place has a sixth to an eighth of a phone's
	 * width: "AI" under the sparkles, because "AI Chats" beside *Search* runs into it on an
	 * administrator's seven-entry bar at 390 px. The link keeps the long name as its accessible name.
	 */
	barLabelKey?: PlainKey
	/**
	 * The module this place belongs to, where it belongs to one. A place is drawn only while the reader
	 * may use its module — the instance runs it and the module admits them — which is the rule every
	 * module contribution obeys (`moduleActive`, `core/modules/slots.ts`). Core's own places name none
	 * and are always there.
	 */
	module?: ModuleId
	/**
	 * The pathname prefixes this place owns, beside its own route. A conversation belongs to Home,
	 * so Home is lit while one is open.
	 */
	matchPaths?: string[]
	/** Only for an administrator; nobody else sees a door that will refuse them. */
	adminOnly?: boolean
	/**
	 * Only for a team member — a member of Team members, administrators included, never a guest.
	 * Before the bootstrap has said who the reader is, it is not drawn: a door that appears and then
	 * vanishes is worse than one that appears a moment later.
	 */
	teamOnly?: boolean
	/** True on the one entry that shows how much is waiting (UX §6.3). */
	badge?: boolean
}

/*
 * The places, in the order a reader meets them. It is a hand-written list although the frontend's
 * own principle dislikes one (CLAUDE.md: never a hardcoded set where the set can grow), and a
 * module contributes no rail slot: the list is of *places*, and a place's order, its icon and
 * whether it survives the 390 px bottom bar are layout decisions of the shell that no module can
 * take on its own — the set is bounded by that bar, not by the number of modules (S-11-D8). A rail
 * slot would move the overflow decision into each module and re-record the 28 full-window pictures
 * for nothing.
 */
const ITEMS: RailItem[] = [
	{ to: "/", icon: "home", labelKey: "shell.nav.home", matchPaths: ["/c/", "/d/"] },
	{ to: "/activity", icon: "activity", labelKey: "shell.nav.activity", badge: true },
	// The address comes from the routes layer, so the row and every link to an AI chat — a card, an
	// activity row, a message — are the same address written once (`routes/links.ts`).
	{
		to: AI_PLACE,
		icon: "aichat",
		labelKey: "shell.rail.aichat",
		barLabelKey: "shell.rail.aichat.bar",
		matchPaths: [`${AI_PLACE}/`],
		module: "aichat",
		teamOnly: true,
	},
	// The drive stands between the AI chats and Search (DV S-01 §3.2). A guest has it too: a group may
	// open a shared drive to them, though they have no My drive.
	{
		to: DRIVE_PLACE,
		icon: "folder",
		labelKey: "shell.rail.drive",
		matchPaths: [`${DRIVE_PLACE}/`],
		module: "drive",
	},
	{ to: "/search", icon: "search", labelKey: "shell.nav.search" },
	{
		to: "/admin/people",
		icon: "admin",
		labelKey: "shell.nav.admin",
		matchPaths: ["/admin/"],
		adminOnly: true,
	},
]

/**
 * Which place the bar's conversation list stands beside. It is Home's neighbour because Home is the
 * screen that says what is waiting and *Chats* the list of where it is waiting: the two entries a
 * reader opens all day stand together, at the start of the bar.
 */
const CHATS_AFTER = "/"

/** The rocket is a picture with its own colours and is drawn as one, never as a stroke icon. */
const ROCKET_SRC = "/icons/rocket.svg"
/** The rocket's drawn box in the head, in CSS pixels; the stylesheet says the same in rem. */
const ROCKET_PX = 32
/** The wordmark under the rocket: the rail's content box minus a step of air on either side. */
const WORDMARK_WIDTH = 56

/**
 * The largest count the badge prints exactly. Not the wire's `CountCap` — that is a chat number and
 * a sidebar row is wide enough for it; here three characters is the whole budget beside a 20 px
 * glyph, and the entry's `aria-label` carries the exact figure regardless.
 */
const BADGE_CAP = 99

/**
 * The places this reader may see, joined into one string — a primitive, so the rail repaints when the
 * answer changes and not whenever the model does. A place stands when the reader's standing lets
 * them in (`core/people/standing.ts`) and the reader may use the module behind it.
 */
function openPlaces(model: Model, registry: ModuleRegistry): string {
	const admin = isAdmin(model.me)
	const teamMember = isTeamMember(model.me)
	return ITEMS.filter(
		(item) =>
			(item.adminOnly !== true || admin) &&
			(item.teamOnly !== true || teamMember) &&
			(item.module === undefined || moduleActive(registry, item.module, model)),
	)
		.map((item) => item.to)
		.join(",")
}

/** Whether a rail entry owns the current pathname: its own route, or one of its prefixes. */
function isActive(item: RailItem, pathname: string): boolean {
	if (pathname === item.to) return true
	return (item.matchPaths ?? []).some((prefix) => pathname.startsWith(prefix))
}

interface RailProps {
	/**
	 * Opens the conversation list. Only the bottom bar has a door to it, and the sheet itself is the
	 * shell's — the frame owns every surface that belongs to no screen in particular, and the rail
	 * owns the entry that asks for one.
	 */
	onOpenChats: () => void
}

export function Rail({ onOpenChats }: RailProps) {
	const { registry } = useShell()
	const open = useModel((model) => openPlaces(model, registry))
	const unread = useModel(unreadBadge)
	const pathname = useRouterState({ select: (state) => state.location.pathname })
	const narrow = useLayoutMode() === "narrow"

	return (
		<nav className="tb-rail" aria-label={t("shell.region.rail")}>
			{/* The head is one link named for what it does, so a screen reader hears the product
			    once; the rocket and the wordmark inside it are decorative. The bottom bar has no
			    room for it, and Home is the bar's first row anyway. */}
			{narrow ? null : (
				<Link to="/" className="tb-rail__head" aria-label={t("shell.nav.brand")}>
					<img
						className="tb-rail__rocket"
						src={ROCKET_SRC}
						alt=""
						width={ROCKET_PX}
						height={ROCKET_PX}
					/>
					<Wordmark variant="brand" width={WORDMARK_WIDTH} />
				</Link>
			)}
			{ITEMS.filter((item) => open.split(",").includes(item.to)).map((item) => {
				const label = t(item.labelKey)
				// The bar's short caption is for the eye; the place's name stays its full one.
				const caption = narrow && item.barLabelKey !== undefined ? t(item.barLabelKey) : label
				const waiting = item.badge === true ? unread : 0
				const current = isActive(item, pathname)
				return (
					<Fragment key={item.to}>
						<Link
							to={item.to}
							className={current ? "tb-rail__item tb-rail__item--active" : "tb-rail__item"}
							// The lime bar and the lit ink say where the reader is to an eye; this says the
							// same to everything else, as the sidebar's rows and every tab strip do.
							aria-current={current ? "page" : undefined}
							// The count is part of the entry's name rather than a second thing to find: a
							// screen reader hears "Activity, 3 unread" and not "Activity" and then "3", and
							// it hears the whole number even where the badge has run out of room for it.
							aria-label={
								waiting > 0
									? t("shell.nav.unread", { label, count: waiting })
									: caption === label
										? undefined
										: label
							}
						>
							<span className="tb-rail__icon">
								<Icon name={item.icon} />
								{/* The product's mention pill, because what it counts are mentions, replies
							    and direct messages; the rail only anchors it to the glyph's corner and
							    rings it in its own ground. Past the cap the pill says so rather than
							    growing: three characters are what fits beside a 20 px drawing in a 72 px
							    column, and nobody triages by the exact number that far down. */}
								{waiting === 0 ? null : (
									<span className="tb-pill tb-pill--mention tb-rail__badge" aria-hidden="true">
										{formatCapped(waiting, BADGE_CAP)}
									</span>
								)}
							</span>
							<span className="tb-rail__label">{caption}</span>
						</Link>
						{/* The list's door, on the bar only: above 768 px the same list is the column
					    beside this one. */}
						{narrow && item.to === CHATS_AFTER ? <ChatsItem onOpen={onOpenChats} /> : null}
					</Fragment>
				)
			})}
			<AccountMenu />
		</nav>
	)
}

/**
 * The bottom bar's entry for the conversation list (VISUAL.md §7.19). It is a button and not a link
 * because what it opens is a sheet over the screen the reader is on: choosing a conversation from it
 * is one step, and the screen behind it is still there while the reader looks.
 *
 * It carries what the list behind it holds — the unread of the reader's own conversations, the same
 * set Home's cards count, and in the accent only when some of it names them (G-71). A count taken
 * over every slice the model holds would promise rows this list does not draw (N4-R2-2).
 */
function ChatsItem({ onOpen }: { onOpen: () => void }) {
	const conversations = useModel((model) => model.conversations)
	const order = useModel((model) => model.conversationOrder)
	const waiting = useMemo(
		() => catchUpTotals({ conversations, conversationOrder: order }),
		[conversations, order],
	)
	const label = t("shell.nav.chats")
	return (
		<button
			type="button"
			className="tb-rail__item tb-rail__chats"
			// The same name-with-its-count as a place that has something waiting, so the number is
			// heard once, as part of the thing it belongs to.
			aria-label={
				waiting.messages === 0
					? undefined
					: t("shell.nav.unread", { label, count: waiting.messages })
			}
			onClick={onOpen}
		>
			<span className="tb-rail__icon">
				<Icon name="messageSquare" />
				{waiting.messages === 0 ? null : (
					<span
						className={cx(
							"tb-pill",
							waiting.mentions > 0 ? "tb-pill--mention" : "tb-pill--count",
							"tb-rail__badge",
						)}
						aria-hidden="true"
					>
						{formatCapped(waiting.messages, BADGE_CAP)}
					</span>
				)}
			</span>
			<span className="tb-rail__label">{label}</span>
		</button>
	)
}
